import { useQuery } from "@tanstack/react-query";
import apiClient from "@shared/services/apiClient";
import { useAuth } from "@shared/context/AuthContext";

export interface DashboardStats {
  patients: {
    total: number;
    active: number;
    inactive: number;
  };
  studies: {
    total: number;
    by_status: {
      completed: number;
      in_progress: number;
      scheduled: number;
    };
    by_priority: {
      stat: number;
      urgent: number;
      routine: number;
    };
  };
  notifications: {
    unread: number;
    critical_alerts: number;
  };
  reports: {
    pending_review: number;
    signed: number;
    total: number;
  };
  appointments?: {
    conflicts_last_30d: number;
    no_show_last_30d: number;
    no_show_rate_last_30d: number;
    upcoming_24h: number;
  };
  processing?: {
    importing_count?: number;
    preprocessing_count?: number;
    processing_count?: number;
    ready_count?: number;
    failed_imports?: number;
    processing_failed_count?: number;
    stuck_recordings?: number;
    stat_eeg_pending?: number;
    studies_completed_today?: number;
  };
  recent_studies: unknown[];
  critical_alerts: unknown[];
  role_specific?: {
    my_studies?: unknown[];
    my_studies_count?: number;
    my_reports?: unknown[];
    my_reports_count?: number;
    queue_count?: number;
    importing_count?: number;
    preprocessing_count?: number;
    processing_count?: number;
    ready_count?: number;
    failed_imports?: number;
    processing_failed_count?: number;
    stuck_recordings?: number;
    stat_eeg_pending?: number;
    studies_completed_today?: number;
  };
}

export interface ClinicalOperationsMetric {
  key: string;
  label: string;
  value: number | string;
  domain: "workflow" | "readiness" | "priority" | "report" | "quality";
  manualLabel: string;
  description: string;
  severity: "neutral" | "info" | "warning" | "critical" | "success";
  drilldown: {
    path: string;
    query?: Record<string, string | number | boolean | undefined>;
  };
  managementActions: string[];
}

export interface ClinicalOperationsLane {
  key: string;
  label: string;
  description: string;
  metrics: ClinicalOperationsMetric[];
}

export interface ClinicalOperationsDashboard {
  generatedAt: string;
  scope: {
    organizationId: string | null;
    role: string;
  };
  copyContract: {
    ui: string;
    manual: string;
  };
  lanes: ClinicalOperationsLane[];
}

const fetchDashboardData = async (): Promise<DashboardStats> => {
  const response = await apiClient.get("/dashboard/user");
  return response.data?.data ?? response.data;
};

const fetchClinicalOperationsDashboard =
  async (): Promise<ClinicalOperationsDashboard> => {
    const response = await apiClient.get("/dashboard/clinical-operations");
    return response.data?.data ?? response.data;
  };

/**
 * Unified dashboard data hook.
 * Calls GET /api/dashboard/user once and distributes data to all widgets,
 * eliminating multiple separate API calls.
 */
export function useDashboardData() {
  const { user } = useAuth();

  const {
    data: stats,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ["dashboardData", user?.id, user?.role],
    queryFn: fetchDashboardData,
    enabled: !!user?.id,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
    refetchInterval: (query) => {
      const processing =
        query.state.data?.processing || query.state.data?.role_specific;
      const pipelineActive =
        Number(processing?.importing_count || 0) > 0 ||
        Number(processing?.preprocessing_count || 0) > 0 ||
        Number(processing?.processing_count || 0) > 0;
      return pipelineActive ? 15 * 1000 : 60 * 1000;
    },
    refetchOnWindowFocus: true,
  });

  return { stats, isLoading, isError, error, refetch };
}

export function useClinicalOperationsDashboardData() {
  const { user } = useAuth();

  const {
    data: dashboard,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ["clinicalOperationsDashboard", user?.id, user?.role],
    queryFn: fetchClinicalOperationsDashboard,
    enabled: !!user?.id,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
    refetchInterval: (query) => {
      const hasActiveDataWork = query.state.data?.lanes
        ?.find((lane) => lane.key === "data")
        ?.metrics.some(
          (metric) =>
            ["queued", "preprocessing", "stuck"].includes(metric.key) &&
            Number(metric.value || 0) > 0,
        );
      return hasActiveDataWork ? 15 * 1000 : 60 * 1000;
    },
    refetchOnWindowFocus: true,
  });

  return { dashboard, isLoading, isError, error, refetch };
}
